import dayjs from 'dayjs';
import get from 'lodash/get';
import { Color } from 'modules/styles/colors';
import { oc } from 'ts-optchain';
import {
  BLOOD_PRESSURE_VITAL_SERVICE_NAME,
  BMI_OBESE,
  BMI_OVERWEIGHT,
  BMI_UNDERWEIGHT,
  BMI_VITAL_SERVICE_NAME,
  HEAD_CIRCUMFERENCE_VITAL_SERVICE_NAME,
  HEART_RATE_VITAL_SERVICE_NAME,
  HEIGHT_VITAL_SERVICE_NAME,
  HIGH_INTERPRETATION,
  LOW_INTERPRETATION,
  NORMAL_INTERPRETATION,
  RESPIRATORY_RATE_VITAL_SERVICE_NAME,
  TEMPERATURE_VITAL_SERVICE_NAME,
  WEIGHT_VITAL_SERVICE_NAME,
  SYSTOLIC,
  DIASTOLIC,
  BLOOD_TYPE_VITAL_SERVICE_NAME
} from './constants';
import { BloodPressureData, ValueUnits, Vital, VitalsBlock } from './types';

export const calcSystolicBpAverage = (data: Vital[]) => {
  return Math.round(
    data.reduce((total: number, next: Vital) => total + Number(next.values[0].valueUnit.value), 0) /
      data.length
  );
};

export const calcDiastolicBpAvereage = (data: Vital[]) => {
  return Math.round(
    data.reduce((total: number, next: Vital) => total + Number(next.values[1].valueUnit.value), 0) /
      data.length
  );
};

// Filter vitals for blood pressure results that contain systolic and diastolic values
export const filterBloodPressureVitals = (data: Vital[] = []): Vital[] => {
  return data.filter(
    item =>
      get(item, 'name', false) &&
      item.name === BLOOD_PRESSURE_VITAL_SERVICE_NAME &&
      get(item, 'values[0].valueUnit', false) &&
      get(item, 'values[1].valueUnit', false)
  );
};

// Filter vitals for blood type
export const filterBloodTypeVitals = (data: Vital[] = []): Vital[] => {
  return data.filter(
    item =>
      get(item, 'name', false) &&
      item.name === BLOOD_TYPE_VITAL_SERVICE_NAME &&
      get(item, 'values[0].valueUnit', false)
  );
};

export const getBloodTypeVitals = (data: Vital[] = []): string => {
  const vitals = filterBloodTypeVitals(data);
  const bloodType = vitals[0]?.values?.[0]?.valueUnit?.value;
  return bloodType || 'Unavailable';
};

export const getStatusColor = (interpretation: string): string => {
  if (
    interpretation === LOW_INTERPRETATION ||
    interpretation === HIGH_INTERPRETATION ||
    interpretation === BMI_UNDERWEIGHT ||
    interpretation === BMI_OVERWEIGHT
  ) {
    return Color.darkOrangeShade;
  }
  if (interpretation === BMI_OBESE) {
    return Color.red;
  }
  if (interpretation === NORMAL_INTERPRETATION) {
    return Color.darkGreenShade;
  }
  return 'transparent';
};

export const getImperialValueUnits = (valueUnits: ValueUnits) => {
  const imperialValueUnits = [];

  if (oc(valueUnits).primaryImperialValue()) {
    imperialValueUnits.push({
      value: valueUnits.primaryImperialValue,
      units: valueUnits.primaryImperialUnit
    });
  }

  if (oc(valueUnits).secondaryImperialValue()) {
    imperialValueUnits.push({
      value: valueUnits.secondaryImperialValue,
      units: valueUnits.secondaryImperialUnit
    });
  }

  return imperialValueUnits;
};

export const normalizedHistoricalVitals = (data: Vital[] = []): VitalsBlock[] => {
  if (data.length > 0) {
    const outputData = [];
    for (const item of data) {
      if (item.name === BLOOD_PRESSURE_VITAL_SERVICE_NAME) {
        if (item.values[0].name === SYSTOLIC || item.values[0].name === DIASTOLIC) {
          const newValues = {
            type: 'BloodPressure',
            [item.values[0].name]: {
              value: Number(item.values[0].valueUnit.value),
              statusColor: getStatusColor(item.values[0].interpretation),
              statusText: item.values[0].interpretation
            },
            [item.values[1].name]: {
              value: Number(item.values[1].valueUnit.value),
              statusColor: getStatusColor(item.values[1].interpretation),
              statusText: item.values[1].interpretation
            },
            date: dayjs(item.effectiveDateTime).format(`M/D/YYYY`),
            units: item.values[1].valueUnit.unit
          };
          outputData.push(newValues);
        }
      } else if (
        item.name === HEIGHT_VITAL_SERVICE_NAME ||
        item.name === WEIGHT_VITAL_SERVICE_NAME ||
        item.name === TEMPERATURE_VITAL_SERVICE_NAME ||
        item.name === HEAD_CIRCUMFERENCE_VITAL_SERVICE_NAME
      ) {
        const valueChange =
          item.name === WEIGHT_VITAL_SERVICE_NAME
            ? {
                value: item.values[0]?.valueChange?.value ?? 'N/A',
                direction: item.values[0]?.valueChange?.direction ?? 0
              }
            : {
                value: 0,
                direction: 0
              };

        const newValues = {
          valueUnits: getImperialValueUnits(item.values[0].valueUnit),
          statusColor: getStatusColor(item.interpretation),
          statusText: item.interpretation,
          date: dayjs(item.effectiveDateTime).format(`YYYY/MM/DD`),
          measureType:
            oc(item).name() === TEMPERATURE_VITAL_SERVICE_NAME ? oc(item).values[0].name() : '',
          valueChange
        };
        outputData.push(newValues);
      } else if (item.name === BMI_VITAL_SERVICE_NAME) {
        const referenceRange = {
          low: item.values[0].ref.find(x => x.interpretation === 'normal').refLow.value,
          high: item.values[0].ref.find(x => x.interpretation === 'normal').refHigh.value
        };
        const valueChange = {
          value: item.values[0].valueChange?.value,
          direction: item.values[0].valueChange?.direction
        };
        const newValues = {
          valueUnits: [
            {
              value: item.values[0].valueUnit.value,
              units: item.values[0].valueUnit.unit
            }
          ],
          statusColor: getStatusColor(item.interpretation),
          statusText: item.interpretation,
          date: dayjs(item.effectiveDateTime).format(`YYYY/MM/DD`),
          referenceRange,
          valueChange
        };
        outputData.push(newValues);
      } else {
        const newValues = {
          valueUnits: [
            {
              value: item.values[0].valueUnit.value,
              units: item.values[0].valueUnit.unit
            }
          ],
          statusColor: getStatusColor(item.interpretation),
          statusText: item.interpretation,
          date: dayjs(item.effectiveDateTime).format(`YYYY/MM/DD`)
        };
        outputData.push(newValues);
      }
    }
    return outputData;
  }
  return data;
};

export const normalizedBloodPressure = (data: Vital[] = []): Nullable<BloodPressureData> => {
  const bpData = filterBloodPressureVitals(data);

  if (bpData.length > 0) {
    return {
      aveDateText: `${dayjs(bpData[bpData.length - 1].effectiveDateTime).format(
        `M/D/YYYY`
      )} - ${dayjs(bpData[0].effectiveDateTime).format(`M/D/YYYY`)}`,
      aveSBP: calcSystolicBpAverage(bpData),
      aveDBP: calcDiastolicBpAvereage(bpData),
      latestDateText: `as of ${dayjs(bpData[0].effectiveDateTime).format(`M/D/YYYY`)}`,
      latestSBP: Number(bpData[0].values[0].valueUnit.value),
      latestDBP: Number(bpData[0].values[1].valueUnit.value),
      data: normalizedHistoricalVitals(bpData)
    };
  }

  return null;
};

// Returns vitals needed for a vitals summary
export const normalizedCoreVitals = (data: Vital[] = []): VitalsBlock[] => {
  const coreVitals = {
    [HEIGHT_VITAL_SERVICE_NAME]: {
      title: 'HEIGHT',
      date: '',
      serviceName: HEIGHT_VITAL_SERVICE_NAME,
      encounterId: ''
    },
    [WEIGHT_VITAL_SERVICE_NAME]: {
      title: 'WEIGHT',
      date: '',
      serviceName: WEIGHT_VITAL_SERVICE_NAME,
      encounterId: ''
    },
    [HEART_RATE_VITAL_SERVICE_NAME]: {
      title: 'HEART RATE',
      date: '',
      serviceName: HEART_RATE_VITAL_SERVICE_NAME,
      encounterId: ''
    },
    [BLOOD_PRESSURE_VITAL_SERVICE_NAME]: {
      title: 'BLOOD PRESSURE',
      date: '',
      serviceName: BLOOD_PRESSURE_VITAL_SERVICE_NAME,
      encounterId: ''
    },
    [TEMPERATURE_VITAL_SERVICE_NAME]: {
      title: 'TEMPERATURE',
      date: '',
      serviceName: TEMPERATURE_VITAL_SERVICE_NAME,
      encounterId: ''
    },
    [RESPIRATORY_RATE_VITAL_SERVICE_NAME]: {
      title: 'RESPIRATORY RATE',
      date: '',
      serviceName: RESPIRATORY_RATE_VITAL_SERVICE_NAME,
      encounterId: ''
    },
    [BMI_VITAL_SERVICE_NAME]: {
      title: 'BMI',
      date: '',
      serviceName: BMI_VITAL_SERVICE_NAME,
      encounterId: ''
    },
    [HEAD_CIRCUMFERENCE_VITAL_SERVICE_NAME]: {
      title: 'HEAD CIRCUMFERENCE',
      date: '',
      serviceName: HEAD_CIRCUMFERENCE_VITAL_SERVICE_NAME,
      encounterId: ''
    }
  };

  // Find and set only the vitals needed for the summary
  let numFound = 0;
  const filteredData = data.filter(item => get(item, 'name', false));
  for (const item of filteredData) {
    let hasValue = false;
    const serviceName = item.name;
    // If the vital is not in the coreVitals list, continue.
    if (coreVitals[serviceName]) {
      // Check if the vital has already been processed
      if (!coreVitals[serviceName].found) {
        // Check if values exist on the vital object
        if (serviceName === BLOOD_PRESSURE_VITAL_SERVICE_NAME) {
          hasValue =
            get(item, 'values[0].valueUnit.value', false) &&
            get(item, 'values[1].valueUnit.value', false);
        } else {
          hasValue = get(item, 'values[0].valueUnit.value', false);
        }
        if (hasValue) {
          coreVitals[serviceName].found = true;
          numFound += 1;
          let newValues = {};

          switch (serviceName) {
            case HEIGHT_VITAL_SERVICE_NAME:
              newValues = {
                valueUnits: getImperialValueUnits(item.values[0].valueUnit),
                date: dayjs(item.effectiveDateTime).format(`M/D/YYYY`),
                encounterId: item.encounterId
              };
              break;
            case WEIGHT_VITAL_SERVICE_NAME:
              newValues = {
                valueUnits: getImperialValueUnits(item.values[0].valueUnit),
                date: dayjs(item.effectiveDateTime).format(`M/D/YYYY`),
                encounterId: item.encounterId
              };
              break;
            case HEART_RATE_VITAL_SERVICE_NAME:
              newValues = {
                valueUnits: [
                  {
                    value: item.values[0].valueUnit.value,
                    units: item.values[0].valueUnit.unit
                  }
                ],
                statusColor: getStatusColor(item.interpretation),
                statusText: item.interpretation,
                date: dayjs(item.effectiveDateTime).format(`M/D/YYYY`),
                encounterId: item.encounterId
              };
              break;
            case BLOOD_PRESSURE_VITAL_SERVICE_NAME:
              newValues = {
                valueUnits: [
                  {
                    value: `${item.values[0].valueUnit.value}/${item.values[1].valueUnit.value}`,
                    units: item.values[0].valueUnit.unit
                  }
                ],
                statusColor: getStatusColor(item.interpretation),
                statusText: item.interpretation,
                date: dayjs(item.effectiveDateTime).format(`M/D/YYYY`),
                encounterId: item.encounterId
              };
              break;
            case TEMPERATURE_VITAL_SERVICE_NAME:
              newValues = {
                valueUnits: getImperialValueUnits(item.values[0].valueUnit),
                statusColor: getStatusColor(item.interpretation),
                statusText: item.interpretation,
                date: dayjs(item.effectiveDateTime).format(`M/D/YYYY`),
                encounterId: item.encounterId
              };
              break;
            case RESPIRATORY_RATE_VITAL_SERVICE_NAME:
              newValues = {
                valueUnits: [
                  {
                    value: item.values[0].valueUnit.value,
                    units: item.values[0].valueUnit.unit
                  }
                ],
                statusColor: getStatusColor(item.interpretation),
                statusText: item.interpretation,
                date: dayjs(item.effectiveDateTime).format(`M/D/YYYY`),
                encounterId: item.encounterId
              };
              break;
            case BMI_VITAL_SERVICE_NAME:
              newValues = {
                valueUnits: [
                  {
                    value: item.values[0].valueUnit.value,
                    units: item.values[0].valueUnit.unit
                  }
                ],
                statusColor: getStatusColor(item.interpretation),
                statusText: item.interpretation,
                date: dayjs(item.effectiveDateTime).format(`M/D/YYYY`),
                encounterId: item.encounterId
              };
              break;
            case HEAD_CIRCUMFERENCE_VITAL_SERVICE_NAME:
              newValues = {
                valueUnits: getImperialValueUnits(item.values[0].valueUnit),
                date: dayjs(item.effectiveDateTime).format(`M/D/YYYY`),
                encounterId: item.encounterId
              };
              break;
            default:
              break;
          }
          // Add new values to vitalsblock object
          coreVitals[serviceName] = { ...coreVitals[serviceName], ...newValues };
        }
      }
      // Break if all the necessary vitals have been found
      if (numFound === Object.keys(coreVitals).length) break;
    }
  }
  // Remove default vitals with no date
  return Object.values(coreVitals).filter(vital => {
    return vital.date !== '';
  });
};
